import Big from "big.js";
import { createSelector } from "reselect";

import { SplitType } from "constants/splits";

import { EMPTY_ARRAY } from "lib";

import { getSplitKey } from "lib/splits";

import {
  selectDefaultBuyerSplitIdsByBusinessIdLookup,
  selectDefaultVendorSplitIdsByBusinessIdLookup,
} from "./indexes";
import {
  createLookupCombiner,
  createLookupSelectors,
  firstObjectMapper,
} from "./lib";
import {
  getBusinesses,
  getDefaultBuyerSplits,
  getDefaultVendorSplits,
  getSaleBuyerSplits,
  getSaleVendorSplits,
} from "./root";

export const [
  selectDefaultVendorSplitByBusinessIdLookup,
  getDefaultVendorSplitByBusinessId,
] = createLookupSelectors(
  [selectDefaultVendorSplitIdsByBusinessIdLookup, getDefaultVendorSplits],
  createLookupCombiner(firstObjectMapper),
);

export const [
  selectDefaultBuyerSplitByBusinessIdLookup,
  getDefaultBuyerSplitByBusinessId,
] = createLookupSelectors(
  [selectDefaultBuyerSplitIdsByBusinessIdLookup, getDefaultBuyerSplits],
  createLookupCombiner(firstObjectMapper),
);

const sortSplitsForDisplay = vendorSplit =>
  // Primary vendor first, then by decreasing amount
  vendorSplit?.splits.sort((a, b) => {
    if (a.businessId === vendorSplit.primaryBusinessId) {
      return -1;
    }
    if (b.businessId === vendorSplit.primaryBusinessId) {
      return 1;
    }
    return -a.amount.localeCompare(b.amount);
  }) || [];

export const getSplitDescription = (vendorSplit, businessLookup) =>
  sortSplitsForDisplay(vendorSplit).map(split => {
    const businessName = businessLookup[split.businessId]?.name || "unknown";
    const amount = new Big(split.amount);
    if (vendorSplit.type === SplitType.HEADCOUNT) {
      return `${amount} hd to ${businessName}`;
    }
    return `${amount.times(100)}% to ${businessName}`;
  });

export const [
  selectDescriptionByVendorSplitIdLookup,
  getDescriptionByVendorSplitId,
] = createLookupSelectors(
  [getSaleVendorSplits, getBusinesses],
  createLookupCombiner(getSplitDescription),
  EMPTY_ARRAY,
);

export const [
  selectDescriptionByBuyerSplitIdLookup,
  getDescriptionByBuyerSplitId,
] = createLookupSelectors(
  [getSaleBuyerSplits, getBusinesses],
  createLookupCombiner(getSplitDescription),
  EMPTY_ARRAY,
);

export const getSplitSummary = (split: VendorSplit | BuyerSplit): string =>
  sortSplitsForDisplay(split)
    .map(sortedSplit => {
      const amount = new Big(sortedSplit.amount);
      if (split.type === SplitType.HEADCOUNT) {
        return `${amount} hd `;
      }
      return `${amount.times(100)}%`;
    })
    .join("/");

export const selectVendorSplitByKeyLookup = createSelector(
  [getSaleVendorSplits],
  (saleVendorSplitLookup: StateModelById<VendorSplit>) =>
    Object.values(saleVendorSplitLookup).reduce<Record<string, VendorSplit>>(
      (acc, split) => {
        const splitKey = getSplitKey(split);
        acc[splitKey] = split;
        return acc;
      },
      {},
    ),
);

export const selectBuyerSplitByKeyLookup = createSelector(
  [getSaleBuyerSplits],
  (saleBuyerSplitLookup: StateModelById<BuyerSplit>) =>
    Object.values(saleBuyerSplitLookup).reduce<Record<string, BuyerSplit>>(
      (lookup, split) => {
        const splitKey = getSplitKey(split);
        lookup[splitKey] = split;
        return lookup;
      },
      {},
    ),
);

export const getVendorSplitByKey = (splitKey: string) => state =>
  selectVendorSplitByKeyLookup(state)[splitKey] || null;

export const getBuyerSplitByKey = (splitKey: string) => state =>
  selectBuyerSplitByKeyLookup(state)[splitKey] || null;
